<template>
  <v-container class="fill-height fullscreen-bg" fluid> 
    <v-row align="center" justify="center">
      <v-col cols="12" sm="8" md="6">
        <v-card v-if="isRedirecting && !errorCode" class="elevation-12 pa-4 text-center">
          <v-card-title class="headline d-block" style="">{{$t('account.preparing-title')}}</v-card-title>
          <v-card-text class="">
            <p>{{$t('account.preparing-text')}}</p>
            <v-progress-circular indeterminate :size="100" :width="5" color="primary" class="mx-a"></v-progress-circular>
          </v-card-text>
        </v-card>
        <v-card v-else-if="isRedirecting && errorCode == 'ALREADY_CONNECTED'" class="elevation-12 pa-4 text-center">
          <v-card-title class="headline d-block" style="">{{$t('account.connect-issue')}}</v-card-title>
          <v-alert type="warning" prominent>
            {{$t('account.already-connected-message')}}
          </v-alert>
          <v-card-text class="">
            <p>{{$t('account.already-connected-description-1')}}</p>
            <p v-if="$route.query.message">{{$route.query.message}}</p>
            <p>{{$t('account.already-connected-description-2')}}</p>
            <v-btn outlined :to="{name: 'profileConnections'}" color="primary">
              {{$t('profile.connections.manage')}}
            </v-btn>
          </v-card-text>
        </v-card>
        <v-card v-else-if="isRedirecting && errorCode" class="elevation-12 pa-4 text-center">
          <v-card-title class="headline d-block" style="">{{$t('account.connect-issue')}}</v-card-title>
          <v-alert v-if="errorCode == 'INVALID_CODE'" type="warning" prominent>The authentication failed, possibly because it took too long, was retried, or was otherwise interrupted.</v-alert>
          <v-alert v-else type="warning" prominent>{{$t('account.connect-issue-message')}}</v-alert>
          <v-card-text class="">
            <p>{{$t('account.connect-issue-description')}}</p>
            <v-btn @click="resetState" color="primary">
              {{$t('shared.retry')}}
            </v-btn>
            <v-btn outlined :to="{name: 'profileConnections'}" color="primary">
              {{$t('profile.connections.manage')}}
            </v-btn>
          </v-card-text>
        </v-card>
        <v-card v-else class="elevation-12">
          <EventHeader v-if="event" :event="event" compact/>
          <v-card-title class="headline">{{$t('account.login.title')}}</v-card-title>

          <div class="provider-container">
            <v-alert v-if="allProvidersVisible" type="info" tile>
              You can log in using a connected account. When you use this option, please make sure to always log back in with the exact same account type.
            </v-alert>

            <v-card-text class="pb-0" :style="{order:providerOrder('terms')}">
              <v-checkbox v-model="termsAccepted" hide-details ref="termsCheckbox">
                <template v-slot:label>
                  <div class="d-flex" style="flex-direction:column;">
                    <i18n path="account.register.terms_privacy_v2" tag="div" class="">
                      <router-link to='privacypolicy'>{{$t('account.register.terms_privacy_privacy')}}</router-link>
                      <router-link to='termsofservice'>{{$t('account.register.terms_privacy_terms')}}</router-link>
                    </i18n>
                    <div class="text-muted">{{$t('account.register.terms_privacy_subtext')}}</div>
                  </div>
                </template>
              </v-checkbox>
            </v-card-text>

            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('strava'), display: providerDisplay('strava')}">
              <v-btn v-if="tenant.enableStrava && providerUrls" :href="providerUrls.strava" block color="#FC4C02" v-on:click="ensureTermsAreApproved">
                <img src="/images/btn_strava_connectwith_orange.svg" style="height:42px"/>
              </v-btn>
            </div>

            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('garmin'), display: providerDisplay('garmin')}">
              <v-btn v-if="providerUrls" :href="providerUrls.garmin" block color="#11BFED" v-on:click="ensureTermsAreApproved">
                <img src="/images/garmin-connect.png" width="26" height="26" alt="Garmin Connect">
                <v-icon  left>mdi-pencil</v-icon> Garmin Connect
              </v-btn>
            </div>

            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('polar'), display: providerDisplay('polar')}">
              <v-btn v-if="providerUrls" :href="providerUrls.polar" block color="#d10027" class="white--text" v-on:click="ensureTermsAreApproved">
                <span style="font-size:70%;">Connect with</span>
                <img src="/images/polar.png" height="22" alt="Polar" class="ml-2">
              </v-btn>
            </div>
            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('fitbit'), display: providerDisplay('fitbit')}">
              <v-btn v-if="providerUrls" :href="providerUrls.fitbit" block color="#4cc2c4" class="white--text" v-on:click="ensureTermsAreApproved">
                <span style="font-size:70%;">Connect with</span>
                <img src="/images/fitbit.png" height="22" alt="Fitbit" class="ml-2">
              </v-btn>
            </div>
            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('suunto'), display: providerDisplay('suunto')}">
              <v-btn v-if="providerUrls" :href="providerUrls.suunto" block color="#147eab" class="white--text" v-on:click="ensureTermsAreApproved">
                <span style="font-size:70%;">Connect with</span>
                <img src="/images/suunto.png" height="24" alt="Suunto" class="ml-2">
              </v-btn>
            </div>
            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('underarmour'), display: providerDisplay('underarmour')}">
              <v-btn v-if="providerUrls" :href="providerUrls.underarmour" block color="#204889" class="white--text" v-on:click="ensureTermsAreApproved">
                <img src="/images/mapmyfitness-white.png" height="20" alt="MapMyFitness" class="mr-2">
                <span style="font-size:80%;" class="mr-1">Log In with</span>
                <strong style="font-size:80%; text-transform:uppercase;">MapMyFitness</strong>
              </v-btn>
            </div>
            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('coros'), display: providerDisplay('coros')}">
              <v-btn v-if="providerUrls" :href="providerUrls.coros" block color="#F8283B" class="white--text" v-on:click="ensureTermsAreApproved">
                <span style="font-size:70%;">Connect with</span>
                <img src="/images/coros.png" height="16" alt="Coros" class="ml-2">
              </v-btn>
            </div>
            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('eventscom'), display: providerDisplay('eventscom')}">
              <v-btn v-if="providerUrls" :href="providerUrls.eventscom" block color="#00BCDA" class="white--text" v-on:click="ensureTermsAreApproved">
                <span style="font-size:70%;">Connect with</span>
                <img src="/images/eventscom-white.png" height="16" alt="Evens.com" class="ml-2">
              </v-btn>
            </div>
            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('concept2'), display: providerDisplay('concept2')}">
              <v-btn v-if="providerUrls" :href="providerUrls.concept2" block color="#033B78" class="white--text" v-on:click="ensureTermsAreApproved">
                <span style="font-size:80%;" class="mr-1">Connect with</span>
                <strong style="font-size:80%; text-transform:uppercase;">Concept2</strong>
              </v-btn>
            </div>
            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('asdeporte'), display: providerDisplay('asdeporte')}">
              <v-btn v-if="providerUrls" :href="providerUrls.asdeporte" block color="#FF6A00" class="white--text" v-on:click="ensureTermsAreApproved">
                <span style="font-size:80%;" class="mr-1">Connect with</span>
                <strong style="font-size:80%; text-transform:uppercase;">Asdeporte</strong>
              </v-btn>
            </div>
            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('slack'), display: providerDisplay('slack')}">
              <v-btn v-if="providerUrls" :href="providerUrls.slack" block color="#2EB67D" class="white--text" v-on:click="ensureTermsAreApproved">
                <v-icon class="mr-2">fab fa-slack</v-icon>
                <span style="font-size:70%;">Sign-In with Slack</span>
              </v-btn>
            </div>
            <div class="ma-4 mb-0 provider provider-connect" :style="{order:providerOrder('microsoft'), display: providerDisplay('microsoft')}">
              <v-btn v-if="providerUrls" :href="providerUrls.microsoft" block color="#F34F1C" class="white--text" v-on:click="ensureTermsAreApproved">
                <v-icon class="mr-2">fab fa-microsoft</v-icon>
                <span style="font-size:70%;">Sign-In with Microsoft</span>
              </v-btn>
            </div>

            <div v-if="!isConnectOnlyMode && !allProvidersVisible" class="ma-4 mb-0 provider provider-connect"  :style="{order:providerOrder('more')}">
              <v-btn block outlined color="grey" class="white--text" @click="showAllProviders()">
                Show More Options
              </v-btn>
            </div>
            <div v-if="allProvidersVisible" class="ma-4 mb-0 provider provider-connect"  :style="{order:providerOrder('more')}">
              <v-btn block outlined color="grey" class="white--text" @click="showDefaultProviders()">
                Show Fewer Options
              </v-btn>
            </div>

            <v-card-text class="provider pb-0" :style="{order:providerOrder('sodisp'), display: providerDisplay('sodisp')}">
              <v-alert v-if="error" type="error">{{error}}</v-alert>
              <v-form ref="form" v-model="valid" lazy-validation>
                <v-text-field 
                  v-model="email" 
                  :rules="emailRules" 
                  :label="$t('account.register.email')" 
                  prepend-icon="fa-envelope"
                  required>
              </v-text-field>

                <v-text-field
                  v-model="password"
                  :rules="passwordRules"
                  :label="$t('account.register.password')"
                  required
                  prepend-icon="fa-lock"
                  :append-icon="passwordShow ? 'fal fa-eye-slash' : 'fal fa-eye'"
                  :type="passwordShow ? 'text' : 'password'"
                  @click:append="passwordShow = !passwordShow"
                ></v-text-field>

              </v-form>
              <v-btn block :disabled="!valid" color="primary" @click="validate" :loading="$store.getters.isLoading">{{$t('account.login.submit')}}</v-btn>
              <v-btn text class="mt-2" color="primary" to="/account/passwordrecovery">{{$t('account.login.forgot-your-password')}}</v-btn>
            </v-card-text>

          </div>
          <i18n v-if="false" path="account.register.terms_privacy" tag="v-card-text" class="text-muted">
            <router-link to='termsofservice'>{{$t('account.register.terms_privacy_terms')}}</router-link>
            <router-link to='privacypolicy'>{{$t('account.register.terms_privacy_privacy')}}</router-link>
          </i18n>
          <v-divider />
          <v-card-text v-if="!isConnectOnlyMode" class="text-center">
            <p>{{$t('account.login.create-account-intro')}}</p>
            <v-btn outlined block color="primary" :to="{ name: 'register', query: $route.query }">{{$t('account.login.create-an-account')}}</v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-if="mfaResolver" v-model="showMfaPhoneNumberDialog" max-width="400px">
      <v-card>
        <v-card-title>Select your phone number</v-card-title>
        <v-card-text>
          Please select the phone number where you'd like to receive the verification code.
        </v-card-text>
        <v-divider></v-divider>
        <v-card-text>
          <v-radio-group v-model="selectedMfaHintIndex">
            <v-radio v-for="(item, idx) in mfaResolver.hints" :key="idx" :label="item.phoneNumber" :value="idx"/>
          </v-radio-group>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn color="secondary" text @click="showMfaPhoneNumberDialog = false">{{$t('shared.cancel')}}</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="secondary" :disabled="selectedMfaHintIndex == null" id="selectPhoneNumberButton" @click="sendMfaVerificationCode();" :loading="$store.getters.isLoading">{{$t('shared.confirm')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showMfaVerificationCodeDialog" max-width="400px">
      <v-card>
        <v-card-title>Enter Verification Code</v-card-title>
        <v-card-text>
          Please enter the verification code which is sent to the phone number you have selected.
        </v-card-text>
        <v-divider></v-divider>
        <v-card-text>
          <v-text-field v-model="mfaVerificationCode" label="Verification Code" hint="You should receive a text message shortly." persistent-hint />
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn color="secondary" text @click="showMfaVerificationCodeDialog = false">{{$t('shared.cancel')}}</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="secondary" :disabled="!mfaVerificationCode" @click="verifyMfaVerificationCode();" :loading="$store.getters.isLoading">{{$t('shared.confirm')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>              

  </v-container>
</template>

<script>
import { getAuth, signInWithCustomToken, signInWithEmailAndPassword } from "firebase/auth";
import { getMultiFactorResolver, PhoneAuthProvider, PhoneMultiFactorGenerator, RecaptchaVerifier } from "firebase/auth";
import { EventBus } from '@/plugins/eventbus.js';
import { mapGetters } from "vuex";
import accountService from "@/services/accountService";
import profileService from "@/services/profileService";
import eventService from "@/services/eventService";
import EventHeader from '@/components/EventHeader.vue';
import tenants from '@/data/tenants.config'
import i18n from '@/i18n'
const tenant = tenants.current();

export default {
  components: {
    EventHeader,
  },
  props: {
  },
  data: () => ({
    tenant: tenant,
    providers: tenant.accountProviders,
    providerUrls: null,
    event: null,
    availableProviders: null,
    allProvidersVisible: false,
    termsAccepted: false,
    passwordShow: false,
    valid: true,
    error: null,
    errorCode: null,
    email: "",
    loggedIn: false,
    mfaResolver: null,
    mfaVerificationId: null,
    mfaVerificationCode: null,
    mfaRecaptchaVerifier: null,
    selectedMfaHintIndex: null,
    showMfaPhoneNumberDialog: false,
    showMfaVerificationCodeDialog: false,
    emailRules: [
      v => !!v || i18n.t('account.register.email-is-required'),
      v => /.+@.+/.test(v) || i18n.t('account.register.email-must-be-valid')
    ],
    password: "",
    passwordRules: [v => !!v || i18n.t('account.register.password-is-required')]
  }),

  async mounted() {
    if (tenant.ssoConfig && !this.$route.query.providers && !this.$route.query.token) {
      // redirect to register page to initiate sso flow, except when this is the response from a auth flow or when an explicit provider needs to be selected
      this.$router.replace({ name: "register", query: { redirect: this.$route.query.redirect } });
      return;
    }
    const auth = getAuth();
    var token = this.$route.query.token;
    this.errorCode = this.$route.query.error;
    if (token && !this.errorCode) {
      // Auto-login from redirects with server-side oauth flows
      var response = await signInWithCustomToken(auth, token);
      if (response && response.user) {
        this.$store.dispatch("setUser", response.user);
        this.$analytics.event('login', { method : 'Token' });

        if (this.$route.query.event) {
          const eventId = this.$route.query.event;
          const status = (await profileService.getEventStatus(eventId)).data;
          if (!status || !status.connected) {
            // Go to join page for additional feedback when it couldn't join (consent or join options)
            this.$router.replace({ name: "eventJoin", params: { id: eventId}, query: {verificationCode: this.$route.query.verificationCode, joinGroupId: this.$route.query.joinGroupId} });
            return;
          }
        }

        this.redirect();
        return;
      }
    }
    if (this.$route.query.providers) {
      this.providers = this.$route.query.providers.replace(' ', '').split(',');
    }
    if (this.$route.query.event) {
      try {
        this.event = (await eventService.get(this.$route.query.event)).data;
      }
      catch {}
      if (this.event && this.event.allowed_providers && this.event.allowed_providers.length > 0 && !this.$route.query.providers) {
        const customProviders = this.event.allowed_providers;
        customProviders.push('sodisp');
        this.providers = customProviders;
      }
      if (this.event && this.event.beta_login) {
        this.providers = ['sodisp'];
      }      
      if (this.isConnectOnlyMode) {
        this.providers = tenant.availableProviders.filter(x => x !== 'sodisp');
      }
    }

    const options = { 
      referral: this.$route.query.referral, 
      eventId: this.$route.query.event, 
      verificationCode: this.$route.query.verificationCode, 
      joinGroupId: this.$route.query.joinGroupId, 
      redirect: this.$route.query.redirect, 
      mode: this.$route.query.mode, 
      view: this.viewMode
    };
    this.providerUrls = this.$oauth.getProviderUrls(options, this.event);

    console.log('Loading providers', this.providers);
    if (this.providers && this.providers.length == 1) {
      const provider = this.providers[0];
      if (this.$oauth.shouldAutoRedirect(provider)) {
        console.log('Only one provider found, auto redirect Redirecting to', this.providerUrls[provider]);
        window.location.href = this.providerUrls[provider];
        return;
      }
    }
    
    EventBus.$on('login-state-change', async user => {
      if (user && !this.errorCode) { 
        await this.redirect();
      }
    });
    if (this.user && !this.errorCode) { 
      await this.redirect();
    }
  },

  methods: {
    providerOrder(provider) {
      if (provider === 'terms') return this.providerOrder('sodisp')+1;
      if (provider === 'more') return 99;
      return this.providers.indexOf(provider);
    },
    providerDisplay(provider) {
      return this.providers.includes(provider) ? '' : 'none';
    },
    resetState() {
      this.errorCode = null;
    },
    ensureTermsAreApproved(event) {
      if (!this.termsAccepted) {
        console.log('///', event);
        if (event) event.preventDefault();
        let elem = this.$refs.termsCheckbox;
        if (elem) {
          elem.$el.classList.add('shake');
          this.$toast.error(i18n.t('account.register.terms_privacy_validation'));
          setTimeout(() => {
            let elem = this.$refs.termsCheckbox;
            if (elem) elem.$el.classList.remove('shake');
          }, 500);
        }
        return false;
      }
      return true;
    },

    showAllProviders() {
      this.availableProviders = this.providers;
      this.providers = tenant.availableProviders ? tenant.availableProviders : tenant.accountProviders;
      this.providers = this.providers.filter(x => x != 'sodisp');
      this.allProvidersVisible = true;
    },
    showDefaultProviders() {
      this.providers = this.availableProviders;
      this.allProvidersVisible = false;
    },


    async validate() {
      if (this.$refs.form.validate()) {
        this.snackbar = true;
        this.loginWithFirebase();
      }
    },

    reset() {
      this.$refs.form.reset();
    },

    async redirect() {
      var force = this.$route.query.force;
      var redirect = this.$route.query.redirect;
      var eventId = this.$route.query.event;
      var verificationCode = this.$route.query.verificationCode;
      var joinGroupId = this.$route.query.joinGroupId;

      if (eventId && this.loggedIn) {
        const joinResult = (await profileService.joinEvent(eventId)).data;
        if (joinResult && joinResult.status !== "OK") {
          // Go to join page for additional feedback when it couldn't join
          this.$router.replace({ name: "eventJoin", params: { id: eventId}, query: {verificationCode: this.$route.query.verificationCode, joinGroupId: this.$route.query.joinGroupId} });
          return;
        }
      }
      
      if (force && !this.loggedIn) {
        console.log('Waiting with redirect until we have done the login');
        // don't yet redirect, wait until we actually logged in
        return;
      }

      if (eventId) {
        if (redirect === 'view-event'){
          this.$router.replace({ name: "event", params: { id: eventId }, query: { referral: this.$route.query.referral, view: this.$route.query.view } });
          return;
        }
        if (redirect === 'manage-event'){
          this.$router.replace({ name: "eventmanagerView", params: { id: eventId } });
          return;
        }
        this.$router.push({ name: 'eventWelcome', params: { id: eventId }, query: {verificationCode:verificationCode, joinGroupId: joinGroupId, view: this.$route.query.view} });
        return;
      }
      if (redirect) {
        if (redirect === 'profile-mfa'){
          this.$router.replace({ name: "profileMfa" });
          return;
        }
        if (redirect === 'profile-onboarding'){
          this.$router.replace({ name: "profileOnboarding" });
          return;
        }
        if (redirect === 'account-link'){
          this.$router.replace({ name: "accountLink" });
          return;
        }
        if (redirect === 'create-event'){
          this.$router.replace({ name: "eventmanagerCreate", query: { referral: this.$route.query.referral, view: this.$route.query.view, slackChannel: this.$route.query.slackChannel } });
          return;
        }
        if (redirect === 'link-event-slack'){
          this.$router.replace({ name: "eventmanagerLinkSlack", query: {slackChannel: this.$route.query.slackChannel} });
          return;
        }
        if (redirect === 'eventmanager'){
          this.$router.replace({ name: "eventmanager", query: { view: this.$route.query.view } });
          return;
        }
        if (redirect === 'manual-entry'){
          this.$router.replace({ name: "activityEntry", query: { view: this.$route.query.view }});
          return;
        }
        if (redirect === 'connections'){
          this.$router.replace({ name: "profileConnections", query: { view: this.$route.query.view, connect: 'success' }});
          return;
        }
        if (redirect === 'profile'){
          this.$router.replace({ name: "profile", query: { view: this.$route.query.view } });
          return;
        }
        else {
          window.location.href = redirect;
        }
      }
      this.$router.replace({ name: "profile", query: { view: this.$route.query.view } });
    },

    async loginWithFirebase() {
      const auth = getAuth();
      const user = {
        email: this.email,
        password: this.password
      };
      try {
        // note: uncomment the next line to force China proxy login flow 
        //throw { code: 'auth/network-request-failed', message: 'Network request failed. '};

        var response = await signInWithEmailAndPassword(auth, user.email, user.password);
        
        await this.$store.dispatch("setUser", response.user);
        this.$analytics.event('login', { method : 'Email' });
        this.loggedIn = true; // so we know we completed the login action

        await this.redirect();
      } catch (ex) {
        if (ex.code == 'auth/network-request-failed') {
          this.tryLoginWithApi();
        }
        else if (ex.code == 'auth/multi-factor-auth-required') {
          this.error = 'Multi-factor authorization is required for your account';
          const auth = getAuth();
          this.mfaResolver = getMultiFactorResolver(auth, ex);
          this.showMfaPhoneNumberDialog = true;
          await this.$nextTick();
          this.mfaRecaptchaVerifier = new RecaptchaVerifier("selectPhoneNumberButton", {
              "size": "invisible",
              "callback": function(response) {
                console.log('recaptchaVerifier resolved.');
              }
          }, auth);

        }
        else {
          this.error = this.$t('account.login.invalid-credentials');
        }
      }
    },

    async tryLoginWithApi() {
      console.log('[auth] Trying proxy login...');
      try {
        var response = await accountService.login(this.email, this.password);
        if (response.status == 200 && response.data && response.data.id_token) {
          console.log('[auth] Successful proxy login. Saving session information.');
          // note: we can't yet store a cookie, so this login session will last at most 1 hour and only in the current tab
          // store id token so we keep using the           
          await this.$store.dispatch("setUserAndToken", { email: this.email, displayName: null, idToken: response.data.id_token, expiresAt: response.data.expires_at });
          this.$analytics.event('login', { method : 'Email' });
          this.loggedIn = true; // so we know we completed the login action
          
          await this.redirect();
        }
      }
      catch (ex) {
        console.log('Error', ex);
        this.error = this.$t('account.login.invalid-credentials');
      }
    },

    async sendMfaVerificationCode() {
      if (this.mfaResolver.hints[this.selectedMfaHintIndex].factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
          // User selected a phone second factor.
        const auth = getAuth();
        console.log('MFA selected second factor.', this.selectedMfaHintIndex, this.mfaResolver.hints[this.selectedMfaHintIndex]);
        //const recaptchaVerifier = new RecaptchaVerifier('recaptcha-container-id', undefined, auth);
        const phoneInfoOptions = {
          multiFactorHint: this.mfaResolver.hints[this.selectedMfaHintIndex],
          session: this.mfaResolver.session
        };
        const phoneAuthProvider = new PhoneAuthProvider(auth);
        this.mfaVerificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, this.mfaRecaptchaVerifier);
        console.log('MFA verify response:', this.mfaVerificationId);
        this.showMfaVerificationCodeDialog = true;
      } else {
        console.log('Unsupported second factor.');
          // Unsupported second factor.
          // Note that only phone second factors are currently supported.
      }

    },

    async verifyMfaVerificationCode() {
      const auth = getAuth();
      const cred = PhoneAuthProvider.credential(this.mfaVerificationId, this.mfaVerificationCode);
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
      console.log('MFA verification request:', cred, multiFactorAssertion);
      const userResponse = await this.mfaResolver.resolveSignIn(multiFactorAssertion);
      console.log('MFA verification response:', userResponse);
      this.loggedIn = true; // so we know we completed the login action
      this.redirect();
    },
  },
  computed: {
    isRedirecting() {
      return this.$route.query.token;
    },
    isConnectOnlyMode() {
      return this.$route.query.mode && this.$route.query.mode === 'connect-only';
    },
    viewMode() {
      // note: somehow vue doesn't correctly read it from route.query so get it manually
      const viewFromUrl = new URLSearchParams(window.location.search).get("view");
      return viewFromUrl || this.$route.query.view || this.$store.state.view;
    },
    ...mapGetters({
      user: "user"
    })
  },
};
</script>
<style lang="scss" scoped>
.view--embedded #content, .view--app #content { min-height: inherit !important;}

.fullscreen-bg {
    margin-top: 60px;
    padding-top: 40px;
    background-image: url(https://sodisp.imgix.net/hero-cycling-1.jpg?w=2000&h=2000&fit=crop);
    background-size: cover;
    background-position: 50%;

    .row { margin: 0!important; }
  }
  .view--embedded .fullscreen-bg { margin-top: 0;}

  .provider-container {
    display: flex;
    flex-direction: column;

  }

  // Shake animation
  @mixin ballb($yaxis: 0) {
    transform: translate3d($yaxis, 0, 0);
  }
  @keyframes bouncein {
    0%, 50% { @include ballb(-5px); }
    25%, 75%, 100% { @include ballb() }
  }
  .shake {
    animation: bouncein 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  }

</style>

